import { Avatar, Spin } from "antd";
import { CheckOutlined, CheckCircleOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { useMemo } from "react";
import type { Message } from "@interfaces/chat.interface";
import { formatTimestamp } from "@utils/formatters";
import { getAvatarGradient, getAvatarLetter } from "@utils/avatarUtils";
import { useChatStore } from "@stores/chatStore";
import { MessageText } from "./MessageText";
import { MessageImageContent } from "./MessageImageContent";
import { MessageVideo } from "./MessageVideo";
import { MessageAudio } from "./MessageAudio";
import { MessageFileContent } from "./MessageFileContent";
import {
  MessageTemplateContent,
  type TemplatePayload,
} from "./MessageTemplateContent";
import { ListMessage } from "./ListMessage";
import { CallMessage } from "./CallMessage";
import { ZaloTemplateContent } from "./ZaloTemplateContent";
import styles from "./styles.module.less";

interface MessageItemProps {
  message: Message;
  isOwn: boolean;
  onDownload?: (url: string) => void;
  showAvatar?: boolean;
  pageId?: string | null;
  showAgentSeparator?: string;
}

// Helper function to check if message is a template
const isTemplateMessage = (message: Message): boolean => {
  if (message.meta?.isTemplate) return true;

  if (
    message.type === "text" &&
    message.body &&
    typeof message.body === "string"
  ) {
    const bodyStr = message.body.trim();
    if (bodyStr.startsWith("{") && bodyStr.includes("templateType")) {
      try {
        const parsed = JSON.parse(bodyStr);
        return !!(
          parsed.templateType &&
          parsed.payload &&
          typeof parsed.payload === "object"
        );
      } catch {
        return false;
      }
    }
  }

  return false;
};

export const MessageItem = ({
  message,
  isOwn,
  onDownload,
  showAvatar = true,
  pageId,
}: MessageItemProps) => {
  const conversations = useChatStore((state) => state.conversations);
  const activeConversation = useMemo(() => {
    return conversations.find((conv) => conv.id === message.conversationId);
  }, [conversations, message.conversationId]);

  // For incoming messages, use sender's avatar; for outgoing, use conversation avatar (though it's hidden by CSS)
  const avatarUrl = isOwn
    ? activeConversation?.avatar
    : message.sender?.avatar || activeConversation?.avatar;
  const avatarName = isOwn
    ? activeConversation?.name
    : message.sender?.name || activeConversation?.name;
  const isTemplate = isTemplateMessage(message);

  const renderMessageContent = (message: Message) => {
    // Handle Zalo CALL_REQUEST type - render as CallMessage component
    if (
      message.meta?.provider === "zalo" &&
      message.meta?.originalMessageType === "CALL_REQUEST"
    ) {
      const callDuration = message.meta?.callDuration as number | undefined;
      return <CallMessage message={message} duration={callDuration} />;
    }

    // Handle Zalo TEMPLATE type - render as ZaloTemplateContent component
    if (
      message.meta?.provider === "zalo" &&
      message.meta?.originalMessageType === "TEMPLATE" &&
      message.meta?.templateUrl
    ) {
      const templateUrl = message.meta.templateUrl as string;
      return <ZaloTemplateContent templateUrl={templateUrl} />;
    }

    // Handle Zalo LIST type - render as ListMessage component
    if (
      message.meta?.provider === "zalo" &&
      message.meta?.originalMessageType === "LIST" &&
      message.meta?.attachmentJson
    ) {
      try {
        const attachmentJson =
          typeof message.meta.attachmentJson === "string"
            ? JSON.parse(message.meta.attachmentJson)
            : message.meta.attachmentJson;

        if (attachmentJson && attachmentJson.attachments) {
          return (
            <ListMessage messageId={message.id} messageData={attachmentJson} />
          );
        }
      } catch (error) {
        console.error("Error parsing attachmentJson for ListMessage:", error);
      }
    }

    if (
      message.attachments &&
      Array.isArray(message.attachments) &&
      message.attachments.length > 0
    ) {
      const fileAttachment = message.attachments.find(
        (att) => att.type === "file"
      );
      if (fileAttachment) {
        return (
          <MessageFileContent
            message={message}
            onDownload={onDownload}
            pageId={pageId}
          />
        );
      }
    }

    const attachments = message.meta?.attachments as
      | Array<{
          type: string;
          payload?: { url?: string };
          url?: string;
        }>
      | undefined;

    if (attachments && Array.isArray(attachments) && attachments.length > 0) {
      const fileAttachment = attachments.find((att) => att.type === "file");
      if (fileAttachment) {
        return (
          <MessageFileContent
            message={message}
            onDownload={onDownload}
            pageId={pageId}
          />
        );
      }
    }

    // Check if message has template in meta
    if (
      message.meta?.isTemplate &&
      message.meta?.templatePayload &&
      message.meta?.templateType
    ) {
      return (
        <MessageTemplateContent
          templateType={message.meta.templateType as string}
          templatePayload={message.meta.templatePayload as TemplatePayload}
        />
      );
    }

    if (
      message.type === "text" &&
      message.body &&
      typeof message.body === "string"
    ) {
      try {
        const bodyStr = message.body.trim();
        if (bodyStr.startsWith("{") && bodyStr.includes("templateType")) {
          const parsedBody = JSON.parse(bodyStr);
          if (
            parsedBody.templateType &&
            parsedBody.payload &&
            typeof parsedBody.payload === "object"
          ) {
            return (
              <MessageTemplateContent
                templateType={parsedBody.templateType}
                templatePayload={parsedBody.payload as TemplatePayload}
              />
            );
          }
        }
      } catch {
        // Not a valid JSON template, continue to render as text
      }
    }

    if (message.type === "text") {
      return (
        <MessageText
          text={message.body as string}
          provider={message.meta?.provider as string | undefined}
        />
      );
    }
    if (message.type === "image") {
      return <MessageImageContent message={message} pageId={pageId} />;
    }
    if (message.type === "video") {
      const videoUrl = (message.body ||
        message.meta?.previewUrl ||
        "") as string;
      return (
        <MessageVideo
          key={`video-${message.id}`}
          src={videoUrl}
          messageId={message.id}
          conversationId={message.conversationId}
          pageId={pageId}
        />
      );
    }
    if (message.type === "audio") {
      const audioUrl = (message.body ||
        message.meta?.previewUrl ||
        "") as string;
      return (
        <MessageAudio
          key={`audio-${message.id}`}
          src={audioUrl}
          messageId={message.id}
          conversationId={message.conversationId}
          pageId={pageId}
        />
      );
    }
    if (message.type === "file") {
      return (
        <MessageFileContent
          message={message}
          onDownload={onDownload}
          pageId={pageId}
        />
      );
    }
    if (message.type === "link") {
      // For non-Zalo link messages, render as text with link
      return (
        <MessageText
          text={message.body as string}
          provider={message.meta?.provider as string | undefined}
        />
      );
    }
    return null;
  };

  return (
    <div
      className={classNames(styles.messageItem, {
        [styles.own]: isOwn,
        [styles.noAvatar]: !showAvatar,
      })}
    >
      {showAvatar ? (
        avatarUrl ? (
          <Avatar src={avatarUrl} size={32} />
        ) : (
          <Avatar
            size={32}
            style={{
              background: getAvatarGradient(avatarName?.charAt(0) || "A"),
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            {getAvatarLetter(avatarName)}
          </Avatar>
        )
      ) : (
        <div style={{ width: 32, flexShrink: 0 }} />
      )}
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.sender}>{avatarName}</span>
        </div>
        <div
          className={classNames(styles.bubble, {
            [styles.imageBubble]: message.type === "image",
            [styles.videoBubble]: message.type === "video",
            [styles.fileBubble]: message.type === "file",
            [styles.listImage]:
              message.meta?.attachments &&
              Array.isArray(message.meta?.attachments) &&
              message.meta?.attachments.length > 1,
            [styles.linkBubble]:
              (message.type === "link" &&
                message.meta?.provider === "zalo" &&
                message.meta?.attachmentJson) ||
              (message.meta?.provider === "zalo" &&
                message.meta?.originalMessageType === "CALL_REQUEST") ||
              (message.meta?.provider === "zalo" &&
                message.meta?.originalMessageType === "TEMPLATE" &&
                message.meta?.templateUrl),
          })}
          style={isTemplate ? { padding: "0px" } : {}}
        >
          {renderMessageContent(message)}
          {isOwn && typeof message.meta?.actorSource === "string" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                opacity: 0.6,
                justifyContent: "flex-end",
                textAlign: "right",
                width: "100%",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  fontStyle: "italic",
                  whiteSpace: "nowrap",
                }}
              >
                {message.meta.actorSource} -{" "}
                {formatTimestamp(message.createdAt)}
              </span>
              <span
                className={classNames(styles.status, styles[message.status])}
                style={isTemplate ? { color: "var(--text-muted-color)" } : {}}
              >
                {message.status === "sending" && <Spin size="small" />}
                {message.status === "sent" && <CheckOutlined />}
                {message.status === "delivered" && <CheckCircleOutlined />}
                {message.status === "read" && (
                  <>
                    <CheckCircleOutlined />
                    <CheckCircleOutlined className={styles.readIcon} />
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
